/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState } from 'react'
import { useTranslation } from 'i18n'
import LegacyRetinaImage from 'components/LegacyRetinaImage'
import { isOfTypeImage } from 'util/quiz-trivia'
import ResultPercentage from './ResultPercentage'
import PlusCircle from 'images/inline/plus-circle.svg'
import ArrowDownCircle from 'images/inline/arrow-down-circle.svg'
import { DropdownButton } from 'steps/referral/parts/DropdownButton2'
import { usePollColors } from './use-poll-colors'

export const AnswerDropdownButton = ({
  label,
  links,
  color,
  labelStyle,
  onLinkClick,
  onDropdownOpen,
  onDropdownLinkClick,
}) => {
  const { t } = useTranslation('poll')
  if (!links?.length) return null

  if (links.length === 1) {
    return (
      <a
        href={links[0].link}
        target="_blank"
        className="image-button-link"
        style={labelStyle}
        onClick={() => onLinkClick?.(links[0].link, links[0].label)}
      >
        {label || links[0].label || t('addToCart')}
        <PlusCircle />
      </a>
    )
  }

  return (
    <DropdownButton
      text={
        <span style={labelStyle}>
          {label || t('shopNow')}
          <ArrowDownCircle />
        </span>
      }
      items={links}
      color={color}
      noBoxShadow
      showArrow={false}
      onDropdownOpen={onDropdownOpen}
      onClick={(_, text, url) => onDropdownLinkClick?.(url, text)}
    />
  )
}

const Question = ({
  question,
  onAnswerClick,
  onAnswerLinkClick,
  onAnswerDropdownClick,
  sponsoredType,
  pollResults,
  totalPollSubmissions,
  resultImage,
  socialShareComponent,
  textAnswered,
  poll,
}) => {
  const [answerSelected, setAnswerSelected] = useState(false)

  const answers = question.answers

  const { getQuestionColors, getAnswerColors } = usePollColors(
    poll,
    sponsoredType,
  )

  const questionColors = getQuestionColors(question)

  const activeColor = questionColors.userSelectionResultBarColor
  const inactiveColor = questionColors.otherSelectionsResultBarColor

  return (
    <div
      className={`poll-question-container ${
        pollResults && !socialShareComponent ? 'has-no-social-share' : ''
      }`}
    >
      <div
        className={`poll-question-list ${
          isOfTypeImage(question) ? ' image-choices' : ''
        }`}
      >
        <ul>
          {answers.map((answer, index) => {
            const links = answer.dropdownButton?.links?.map(link => ({
              ...link,
              link: link.url,
            }))

            const answerColors = getAnswerColors(answer)

            return (
              <li
                tabIndex={0}
                role="button"
                key={`answer-${index}`}
                className={`${
                  !sponsoredType ? 'editorial-answer' : ''
                } answer-index-${index} ${
                  answerSelected === index ? 'selected' : ''
                } ${pollResults ? 'is-answered' : ''} ${
                  !!answer.text ? 'has-text' : ''
                } ${!!pollResults ? 'has-results' : ''}`}
                onClick={() => {
                  if (pollResults) return

                  setAnswerSelected(index)
                  onAnswerClick({
                    questionId: question.id,
                    answerId: answer.id,
                  })
                }}
                style={{
                  '--poll-answer-border-color': answerColors.answerBgColor,
                  '--poll-answer-text-color': answerColors.answerTextColor,
                  '--poll-answer-border-color-hover':
                    answerColors.answerBgHoverColor,
                  '--poll-answer-dropdown-button-bg-color':
                    answerColors.dropdownButtonBgColor,
                  '--poll-answer-dropdown-button-text-color':
                    answerColors.dropdownButtonTextColor,
                  '--poll-answer-dropdown-button-bg-hover-color':
                    answerColors.dropdownButtonBgHoverColor,
                }}
              >
                {answer.puckAnswer ? (
                  // Puck: generic slot rendering — no hardcoded type/image/dropdown
                  answer.beforePercentageContent ? (
                    <div className="image-button">
                      <div className="image-choice">
                        {answer.beforePercentageContent}
                      </div>
                      <div className={`image-percentage-container ${!answer.text ? 'image-only' : ''}`}>
                        {answer.text && <span style={answer.puckTextStyle} dangerouslySetInnerHTML={{ __html: answer.text }} />}
                        <ResultPercentage
                          answer={answer}
                          pollResults={pollResults}
                          totalPollSubmissions={totalPollSubmissions}
                          resultImage={resultImage}
                          activeColor={activeColor}
                          inactiveColor={inactiveColor}
                          textAnswered={textAnswered}
                        />
                      </div>
                      {!!pollResults && answer.afterPercentageContent}
                    </div>
                  ) : (
                    <>
                      {answer.text && <span style={answer.puckTextStyle} dangerouslySetInnerHTML={{ __html: answer.text }} />}
                      <ResultPercentage
                        answer={answer}
                        pollResults={pollResults}
                        totalPollSubmissions={totalPollSubmissions}
                        resultImage={resultImage}
                        activeColor={activeColor}
                        inactiveColor={inactiveColor}
                        textAnswered={textAnswered}
                      />
                      {!!pollResults && answer.afterPercentageContent}
                    </>
                  )
                ) : (
                  // Non-puck: legacy TEXT/IMAGE rendering (backward compat)
                  <>
                    {answer.type === 'TEXT' && (
                      <>
                        <span dangerouslySetInnerHTML={{ __html: answer.text }} />
                        <ResultPercentage
                          answer={answer}
                          pollResults={pollResults}
                          totalPollSubmissions={totalPollSubmissions}
                          resultImage={resultImage}
                          activeColor={activeColor}
                          inactiveColor={inactiveColor}
                          textAnswered={textAnswered}
                        />
                        {!!answer.dropdownButton && !!pollResults && (
                          <AnswerDropdownButton
                            label={answer.dropdownButton.label}
                            links={links}
                            color={answerColors.dropdownButtonBgColor}
                            onLinkClick={(link, linkTitle) =>
                              onAnswerLinkClick({
                                questionId: question.id,
                                answerId: answer.id,
                                link,
                                linkTitle,
                              })
                            }
                            onDropdownOpen={() =>
                              onAnswerDropdownClick({
                                questionId: question.id,
                                answerId: answer.id,
                                dropdownTitle: answer.dropdownButton.label,
                              })
                            }
                            onDropdownLinkClick={(link, linkTitle) =>
                              onAnswerLinkClick({
                                questionId: question.id,
                                answerId: answer.id,
                                link,
                                linkTitle,
                              })
                            }
                          />
                        )}
                      </>
                    )}
                    {answer.type === 'IMAGE' && (
                      <div className="image-button">
                        <div className="image-choice">
                          <LegacyRetinaImage image={answer.image} />
                        </div>
                        {(!!answer.text || !!pollResults) && (
                          <>
                            <div
                              className={`image-percentage-container ${
                                !answer.text ? 'image-only' : ''
                              }`}
                            >
                              {!!answer.text && (
                                <span dangerouslySetInnerHTML={{ __html: answer.text }} />
                              )}
                              <ResultPercentage
                                answer={answer}
                                pollResults={pollResults}
                                totalPollSubmissions={totalPollSubmissions}
                                resultImage={resultImage}
                                activeColor={activeColor}
                                inactiveColor={inactiveColor}
                                textAnswered={textAnswered}
                              />
                            </div>
                            {!!answer.dropdownButton && !!pollResults && (
                                <AnswerDropdownButton
                                  label={answer.dropdownButton.label}
                                  links={links}
                                  color={answerColors.dropdownButtonBgColor}
                                  onLinkClick={(link, linkTitle) =>
                                    onAnswerLinkClick({
                                      questionId: question.id,
                                      answerId: answer.id,
                                      link,
                                      linkTitle,
                                    })
                                  }
                                  onDropdownOpen={() =>
                                    onAnswerDropdownClick({
                                      questionId: question.id,
                                      answerId: answer.id,
                                      dropdownTitle: answer.dropdownButton.label,
                                    })
                                  }
                                  onDropdownLinkClick={(link, linkTitle) =>
                                    onAnswerLinkClick({
                                      questionId: question.id,
                                      answerId: answer.id,
                                      link,
                                      linkTitle,
                                    })
                                  }
                                />
                              )}
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}
              </li>
            )
          })}
        </ul>
      </div>
      {pollResults && socialShareComponent}
    </div>
  )
}
export default Question
